import { AxiosPromise } from 'axios';
import { saveAs } from 'file-saver';

import {
  BaseRepository,
  IEnumerableResponse,
  ITableRequestParams
} from '@app/src/common/repository/BaseRepository';
import {
  IEventContact,
  IEventContactName
} from '@app/src/components/event-management/model/Event';

export interface IUpdateContactDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  description: string;
  address: string;
}

export interface ICreateContactCommand {
  address: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  description: string;
}

export interface IBaseHashParams {
  startIndex?: number;
  endIndex?: number;
  search?: string;
  filter?: string | {};
  includeAssets?: boolean;
}

export class ContactsRepository extends BaseRepository {
  contactsUrl = `${this.ereceptionstUrl}/contacts`;
  contactsNamesUrl = `${this.contactsUrl}/names`;

  public getContacts(
    hashParams?: IBaseHashParams
  ): Promise<IEnumerableResponse<IEventContact>> {
    return this.callGet<IEnumerableResponse<IEventContact>, IBaseHashParams>(
      `${this.contactsUrl}`,
      hashParams
    ).then(response => response.data);
  }

  public getContactNames(params?: {
    showInPhonebook: boolean;
  }): Promise<IEventContactName[]> {
    return this.callGet<IEventContactName[]>(
      this.contactsNamesUrl,
      params
    ).then(response => response.data);
  }

  public updateContact(params: IUpdateContactDto): AxiosPromise<IEventContact> {
    return this.callPut<IEventContact>(
      `${this.contactsUrl}/${params.id}`,
      params
    );
  }

  public createContact(params: ICreateContactCommand): AxiosPromise<string> {
    return this.callPost<string>(`${this.contactsUrl}`, params);
  }

  public getContact(
    id: string,
    hashParams?: IBaseHashParams
  ): AxiosPromise<IEventContact> {
    return this.callGet<IEventContact, IBaseHashParams>(
      `${this.contactsUrl}/${id}`,
      hashParams
    );
  }

  public getContactIds(params?: ITableRequestParams): Promise<string[]> {
    return this.callGet<string[], ITableRequestParams>(
      `${this.contactsUrl}/ids`,
      params
    ).then(response => response.data);
  }

  public deleteContact(contactId?: string): AxiosPromise<void> {
    const deleteCondition = contactId ? `/${contactId}` : '';
    return this.callDelete(`${this.contactsUrl}${deleteCondition}`);
  }

  public getContactImportTemplate(fileName: string, language: string) {
    return this.callGet<Blob>(
      `${this.contactsUrl}/download-import-template`,
      {
        params: {
          language
        }
      },
      {
        responseType: 'blob'
      }
    ).then(({ data }) => {
      saveAs(data, fileName);
    });
  }

  public downloadContacts(fileName: string, params?: IBaseHashParams) {
    return this.callGet<Blob>(`${this.contactsUrl}/download`, params, {
      responseType: 'blob'
    }).then(({ data }) => {
      saveAs(data, fileName);
    });
  }

  public deleteBulkContacts(ids: string[]): AxiosPromise<void> {
    return this.callDelete(`${this.contactsUrl}/ids`, { ids });
  }

  public syncExternalContacts(): AxiosPromise<void> {
    return this.callPost<void>(`${this.contactsUrl}/import-from-external`);
  }
}

export default new ContactsRepository();
